import { useLocation, useNavigate } from "react-router-dom";
import { 
  LayoutDashboard, 
  FolderKanban, 
  CheckSquare, 
  BarChart3, 
  Users,
  ChevronLeft,
  ChevronRight,
  BookOpen,
  Building2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslation } from "@/i18n/translations";
import { useAuth } from "@/contexts/AuthContext";
import { useSeatAccessStatusQuery } from "@/hooks/queries/useSeatsQuery";
import BrandMark from "../auth/BrandMark";

const AppSidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => window.innerWidth < 768);
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const { isAuthenticated } = useAuth();
  const { data: seatAccessStatus } = useSeatAccessStatusQuery(isAuthenticated);
  const hasSeatAccess = seatAccessStatus?.hasAccess ?? false;

  const menuItems = [
    { title: t("dashboard"), id: "dashboard", icon: LayoutDashboard, path: "/dashboard" },
    { title: t("organizations"), id: "organization", icon: Building2, path: "/organization" },
    // Projects/Reports require an organization with a usable seat pool (active seats
    // not exceeding available seats and at least one active seat) — hidden until confirmed.
    ...(hasSeatAccess ? [{ title: t("projects"), id: "project", icon: FolderKanban, path: "/project" }] : []),
    // { title: t("tasks"), id: "tasks", icon: CheckSquare, path: "/tasks" },
    ...(hasSeatAccess ? [{ title: t("reports"), id: "reports", icon: BarChart3, path: "/reports" }] : []),
    { title: t("technicalDocumentation"), id: "technical-docs", icon: BookOpen, path: "/technical-docs" },
    // { title: t("usersRoles"), id: "users", icon: Users, path: "/users" },
  ];

  const activeItem = menuItems.find((item) =>
    location.pathname === item.path || location.pathname.startsWith(item.path + "/")
  )?.id || "dashboard";

  const handleNavigation = (item: typeof menuItems[0]) => {
    navigate(item.path);
  };

  return (
    <aside
      className={cn(
        "h-screen bg-sidebar text-sidebar-foreground flex flex-col transition-all duration-300 border-r border-sidebar-border",
        collapsed ? "w-16" : "w-64"
      )}
    >
      {/* Logo */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-sidebar-border">
        {collapsed ? (
          <img src="/favicon-32x32.png" alt="Veritas logo" className="w-8 h-8 rounded-lg" />
        ) : (
          <div className="flex items-center gap-2">
            {/*<img src="/favicon-32x32.png" alt="Veritas logo" className="w-8 h-8 rounded-lg" />*/}
            <BrandMark className="h-8 w-8" />
            <span className="font-semibold text-lg">Veritas</span>
          </div>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setCollapsed(!collapsed)}
          className="text-sidebar-foreground hover:bg-sidebar-accent"
        >
          {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </Button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 px-2">
        <ul className="space-y-1">
          {menuItems.map((item) => {
            const isActive = activeItem === item.id;
            return (
              <li key={item.id}>
                <button
                  onClick={() => handleNavigation(item)}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
                    isActive
                      ? "bg-sidebar-accent text-sidebar-accent-foreground"
                      : "text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
                  )}
                >
                  <item.icon size={20} />
                  {!collapsed && <span className="font-medium">{item.title}</span>}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>
    </aside>
  );
};

export default AppSidebar;
